import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Tag, ArrowLeft, Loader2, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { API } from '../App';

const BlogDetail = () => {
    const { id } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                const res = await axios.get(`${API}/api/blogs/${id}`);
                setBlog(res.data);
            } catch (error) {
                console.error("Failed to fetch blog:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlog();
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20">
                <Loader2 className="w-12 h-12 text-primary animate-spin" />
            </div>
        );
    }

    if (!blog) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center pt-20 text-center px-4">
                <h2 className="text-3xl font-bold mb-4">Article Not Found</h2>
                <p className="text-muted-foreground mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
                <Link to="/blog">
                    <Button>Return to Blog</Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-16">
            {/* Hero Image */}
            <div className="w-full h-[40vh] md:h-[60vh] relative -mt-14 pt-14 md:mt-0 md:pt-0">
                <img 
                    src={blog.image_url || "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200"} 
                    alt={blog.title} 
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                
                <div className="absolute inset-0 flex items-end pb-12">
                    <div className="container-solar w-full">
                        <Link to="/blog" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back to all articles
                        </Link>
                        
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            <Badge className="bg-primary hover:bg-primary/90 text-white text-sm capitalize">
                                {blog.category}
                            </Badge>
                            {blog.tags && blog.tags.map(tag => (
                                <Badge key={tag} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                                    {tag}
                                </Badge>
                            ))}
                        </div>
                        
                        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 max-w-4xl leading-tight">
                            {blog.title}
                        </h1>
                        
                        <div className="flex flex-wrap items-center gap-6 text-white/90 text-sm md:text-base">
                            <span className="flex items-center gap-2">
                                <User className="w-5 h-5" />
                                {blog.author_name}
                            </span>
                            <span className="flex items-center gap-2">
                                <Calendar className="w-5 h-5" />
                                {new Date(blog.created_at).toLocaleDateString(undefined, {
                                    year: 'numeric', month: 'long', day: 'numeric'
                                })}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="container-solar mt-12">
                <div className="grid lg:grid-cols-[1fr_300px] gap-12">
                    {/* Main Article */}
                    <article className="prose prose-lg prose-slate max-w-none">
                        <p className="lead text-xl text-muted-foreground font-medium mb-8">
                            {blog.excerpt}
                        </p>
                        
                        <div className="whitespace-pre-wrap text-foreground/90 leading-relaxed space-y-6">
                            {blog.content}
                        </div>
                    </article>

                    {/* Sidebar */}
                    <aside className="space-y-8 lg:sticky lg:top-24 h-max">
                        {/* Share Widget */}
                        <div className="bg-card rounded-2xl border p-6 shadow-sm">
                            <h3 className="font-bold flex items-center gap-2 mb-4 text-lg">
                                <Share2 className="w-5 h-5 text-primary" />
                                Share this article
                            </h3>
                            <div className="flex gap-2">
                                <Button variant="outline" size="icon" className="rounded-full hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200">
                                    <Facebook className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="icon" className="rounded-full hover:bg-sky-50 hover:text-sky-500 hover:border-sky-200">
                                    <Twitter className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="icon" className="rounded-full hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200">
                                    <Linkedin className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Quick CTA */}
                        <div className="solar-gradient rounded-2xl p-6 text-white text-center shadow-lg">
                            <h3 className="text-xl font-bold mb-3">Ready to go solar?</h3>
                            <p className="text-white/80 text-sm mb-6">
                                Get a free consultation and see how much you can save on your energy bills.
                            </p>
                            <Link to="/calculator">
                                <Button className="w-full bg-white text-primary hover:bg-white/90">
                                    Calculate Savings
                                </Button>
                            </Link>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;
